import { useMemo } from "react";

import { Stack } from "@mui/material";
import List from "@mui/material/List";

import { AmountInCustomerCurrency } from "../AmountInCustomerCurrency";
import { CardPager } from "../CardPager";
import EmptyList from "../EmptyList";
import { EnableRealTime } from "../EnableRealTime";
import HomePageCard from "../HomePageCard";
import { AnomalyRow } from "../Rows/AnomalyRow";
import { type AnomalyWithCostAndRef } from "../types";
import { ITEMS_PER_PAGE, usePagination } from "./hooks";

type ActiveAnomaliesListProps = {
  anomalies: AnomalyWithCostAndRef[];
};

export const ActiveAnomaliesList = ({ anomalies }: ActiveAnomaliesListProps) => {
  const totalCost = useMemo(
    () => anomalies.filter((anomaly) => anomaly.status === "ACTIVE").reduce((acc, anomaly) => acc + anomaly.cost, 0),
    [anomalies]
  );

  const { currentPageItems, currentPage, startItem, endItem, totalItems, handlePageChange } = usePagination(anomalies);

  if (anomalies.length === 0) {
    return (
      <EmptyList
        title="No cost anomalies to action yet"
        subheader="Reduce risk by taking action and acknowledging anomalies"
        description="When cost anomalies take place on your cloud environment you will be able to track them here and take action to reduce waste and risk"
        buttonComponent={<EnableRealTime />}
      />
    );
  }

  return (
    <HomePageCard
      title="Anomalies over the last 90 days"
      subheader={
        totalCost > 0 ? (
          <>
            Ignoring active anomalies could result in at least{" "}
            <b>
              <AmountInCustomerCurrency amount={totalCost} />
            </b>{" "}
            of costs
          </>
        ) : (
          <>Ignoring active anomalies could result in unnecessary costs</>
        )
      }
      sxProps={{ height: "100%" }}
    >
      <Stack sx={{ flex: 1, justifyContent: "space-between" }}>
        <List dense sx={{ pb: 0, pt: 0, "& .MuiListItem-root:last-of-type": { borderBottom: "none" } }}>
          {currentPageItems.map((anomaly, i) => (
            <AnomalyRow
              key={`${anomaly.metadata.alert_id}-${i}`}
              anomaly={anomaly}
              hideDivider={i === currentPageItems.length - 1 && currentPageItems.length === ITEMS_PER_PAGE}
            />
          ))}
        </List>
        <CardPager
          startItem={startItem}
          endItem={endItem}
          totalItems={totalItems}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </Stack>
    </HomePageCard>
  );
};
